import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  Avatar,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Container,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import { SKILLS, INTERESTS, LOCATION_PREFERENCES } from '@/constants/options';
import type { Skill, Interest, LocationPreference } from '@/constants/options';
import type { ProfileData, StudentProfileResponse } from '@/types/student';
import {
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Lightbulb as LightbulbIcon,
  DirectionsWalk as TravelIcon,
  Home as HomeIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { formInputStyles, numberInputProps } from '@/styles/formStyles';
import { useToast } from '@/components/common/Toast';

export const ProfilePage = () => {
  const { refreshProfile } = useUser();
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    locationPreference: 'Any',
    bio: '',
    careerGoals: '',
    skills: [],
    interests: [],
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    latitude: undefined,
    longitude: undefined,
    totalHoursCompleted: 0,
    maxTravelDistanceKm: undefined,
    preferredTravelMethod: undefined,
    maxTravelTimeMinutes: undefined,
    neighborhood: undefined,
    school: {
      id: 0,
      name: 'School not set',
      district: {
        id: 0,
        name: ''
      }
    }
  });

  const toast = useToast();
  const { data, isLoading: queryLoading, error, refetch } = useQuery({
    queryKey: ['studentProfile'],
    queryFn: async () => {
      const response = await api.profile.getStudentProfile();
      return response.data;
    },
  });

  // Update form data when query succeeds
  useEffect(() => {
    if (data) {
      console.log('Updating form data with profile:', data);
      const studentProfile = data.student_profile as StudentProfileResponse;
      if (!studentProfile) {
        console.warn('No student profile data found');
        return;
      }

      setFormData({
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        locationPreference: studentProfile.location_preference || 'Any',
        bio: studentProfile.bio || '',
        careerGoals: studentProfile.career_goals || '',
        skills: studentProfile.skills || [],
        interests: studentProfile.interests || [],
        streetAddress: studentProfile.street_address || '',
        city: studentProfile.city || '',
        state: studentProfile.state || '',
        postalCode: studentProfile.postal_code || '',
        country: studentProfile.country || '',
        latitude: studentProfile.latitude,
        longitude: studentProfile.longitude,
        totalHoursCompleted: studentProfile.total_hours_completed || 0,
        maxTravelDistanceKm: studentProfile.max_travel_distance_km,
        preferredTravelMethod: studentProfile.preferred_travel_method,
        maxTravelTimeMinutes: studentProfile.max_travel_time_minutes,
        neighborhood: studentProfile.neighborhood,
        school: data.school && data.school.district ? {
          id: data.school.id,
          name: data.school.name,
          district: {
            id: data.school.district.id,
            name: data.school.district.name
          }
        } : {
          id: 0,
          name: 'School not set',
          district: {
            id: 0,
            name: ''
          }
        }
      });
    }
  }, [data]);

  // Update profile mutation
  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: async (updatedData: ProfileData) => {
      console.log('Updating student profile with data:', updatedData);
      try {
        const response = await api.profile.updateStudentProfile({
          bio: updatedData.bio,
          career_goals: updatedData.careerGoals,
          skills: updatedData.skills,
          interests: updatedData.interests,
          location_preference: updatedData.locationPreference,
          street_address: updatedData.streetAddress,
          city: updatedData.city,
          state: updatedData.state,
          postal_code: updatedData.postalCode,
          country: updatedData.country,
          latitude: updatedData.latitude,
          longitude: updatedData.longitude,
          max_travel_distance_km: updatedData.maxTravelDistanceKm,
          preferred_travel_method: updatedData.preferredTravelMethod,
          max_travel_time_minutes: updatedData.maxTravelTimeMinutes,
          neighborhood: updatedData.neighborhood
        });
        console.log('Profile update response:', response);
        await refreshProfile();
      } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('Profile updated successfully');
      setIsEditing(false);
      refetch(); // Refetch the profile data after successful update
    },
    onError: (error) => {
      console.error('Error in update mutation:', error);
      toast.showError(error);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Submitting profile update with data:', formData);
      await updateProfile(formData);
      toast.showSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.showError(error);
    }
  };

  const handleLocationPreferenceChange = (e: SelectChangeEvent) => {
    const value = e.target.value as LocationPreference;
    setFormData({ ...formData, locationPreference: value });
  };

  const handleNumberInput = (
    value: string,
    field: keyof ProfileData,
    min?: number,
    max?: number
  ) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numValue = value === '' ? undefined : Number(value);
      if (numValue === undefined || ((!min || numValue >= min) && (!max || numValue <= max))) {
        setFormData(prev => ({ ...prev, [field]: numValue }));
      }
    }
  };

  if (queryLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Error in profile page:', error);
    return (
      <Alert severity="error">
        Failed to load profile. Please try again later.
        {process.env.NODE_ENV === 'development' && (
          <pre>{JSON.stringify(error, null, 2)}</pre>
        )}
      </Alert>
    );
  }

  return (
    <Box>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            borderRadius: '16px',
            overflow: 'hidden',
            mb: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            ...formInputStyles(theme),
          }}
        >
          {/* Banner Section */}
          <Box 
            sx={{ 
              background: theme => `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              position: 'relative',
              py: 3
            }}
          >
            <Box sx={{ position: 'relative', px: { xs: 3, md: 4 } }}>
              {/* Main Info Section */}
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  {/* Avatar and Name */}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{ 
                        width: 56, 
                        height: 56,
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        border: '2px solid white'
                      }}
                    >
                      {formData.firstName?.[0]}{formData.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          mb: 0.5
                        }}
                      >
                        {formData.firstName} {formData.lastName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SchoolIcon sx={{ color: 'white', fontSize: '0.9rem', opacity: 0.9 }} />
                          <Typography sx={{ color: 'white', opacity: 0.9, fontSize: '0.875rem' }}>
                            {formData.school?.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ color: 'white', fontSize: '0.9rem', opacity: 0.9 }} />
                          <Typography sx={{ color: 'white', opacity: 0.9, fontSize: '0.875rem' }}>
                            {formData.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Stack>

                  {/* Edit Button */}
                  <Button
                    startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                    variant="contained"
                    size="small"
                    onClick={isEditing ? handleSubmit : () => setIsEditing(true)}
                    disabled={isPending}
                    sx={{ 
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: 'white',
                        opacity: 0.9
                      },
                      fontSize: '0.875rem',
                      px: 2,
                      py: 0.75,
                      minWidth: 'auto',
                      height: 'fit-content'
                    }}
                  >
                    {isEditing ? 'Save' : 'Edit'}
                  </Button>
                </Stack>
              </Box>

              {/* Stats Section */}
              <Box 
                sx={{ 
                  display: 'flex',
                  gap: 3,
                  flexWrap: 'wrap',
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {/* Hours Stat */}
                <Box sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <AccessTimeIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
                  <Box>
                    <Typography sx={{ 
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: 600,
                      lineHeight: 1
                    }}>
                      {Math.round(Number(formData.totalHoursCompleted || 0))} Hours
                    </Typography>
                    <Typography sx={{ 
                      color: 'white',
                      fontSize: '0.75rem',
                      opacity: 0.8,
                      lineHeight: 1,
                      mt: 0.5
                    }}>
                      Volunteer Time
                    </Typography>
                  </Box>
                </Box>

                {/* Location Stat */}
                <Box sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <LocationIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
                  <Box>
                    <Typography sx={{ 
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: 600,
                      lineHeight: 1
                    }}>
                      {formData.city}, {formData.state}
                    </Typography>
                    <Typography sx={{ 
                      color: 'white',
                      fontSize: '0.75rem',
                      opacity: 0.8,
                      lineHeight: 1,
                      mt: 0.5
                    }}>
                      Location
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={4}>
              {/* Left Column - About Me */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon /> About Me
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Tell us about yourself and your experiences"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      disabled={!isEditing}
                      sx={{ mb: 3 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon /> Career Goals
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Where do you see yourself in 5 years?"
                      value={formData.careerGoals}
                      onChange={(e) => setFormData({ ...formData, careerGoals: e.target.value })}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LightbulbIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Skills & Interests */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon /> Skills & Interests
                    </Typography>
                    <Stack spacing={3}>
                      <Autocomplete
                        multiple
                        options={SKILLS}
                        value={formData.skills}
                        onChange={(_: any, newValue: Skill[]) => setFormData({ ...formData, skills: newValue })}
                        disabled={!isEditing}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Skills"
                            helperText="Select skills you'd like to develop"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <LightbulbIcon color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        }
                      />
                      <Autocomplete
                        multiple
                        options={INTERESTS}
                        value={formData.interests}
                        onChange={(_: any, newValue: Interest[]) => setFormData({ ...formData, interests: newValue })}
                        disabled={!isEditing}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Interests"
                            helperText="Select areas you're interested in"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <LightbulbIcon color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              color="secondary"
                              variant="outlined"
                            />
                          ))
                        }
                      />
                      <FormControl fullWidth disabled={!isEditing}>
                        <InputLabel>Location Preference</InputLabel>
                        <Select
                          value={formData.locationPreference}
                          label="Location Preference"
                          onChange={handleLocationPreferenceChange}
                          startAdornment={
                            <InputAdornment position="start">
                              <LocationIcon color="action" />
                            </InputAdornment>
                          }
                        >
                          {LOCATION_PREFERENCES.map((pref) => (
                            <MenuItem key={pref.value} value={pref.value}>
                              {pref.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Card elevation={0}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HomeIcon /> Address Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Street Address"
                          value={formData.streetAddress}
                          onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="City"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="State/Province"
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Postal Code"
                          value={formData.postalCode}
                          onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Country"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          disabled={!isEditing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      {/* Enhanced Location Preferences */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TravelIcon /> Travel Preferences
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Neighborhood/Area"
                          value={formData.neighborhood || ''}
                          onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                          disabled={!isEditing}
                          helperText="Your general neighborhood or area"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PlaceIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Maximum Travel Distance (km)"
                          value={formData.maxTravelDistanceKm || ''}
                          onChange={(e) => handleNumberInput(e.target.value, 'maxTravelDistanceKm', 0, 100)}
                          disabled={!isEditing}
                          helperText="How far are you willing to travel?"
                          error={formData.maxTravelDistanceKm !== undefined && (formData.maxTravelDistanceKm < 0 || formData.maxTravelDistanceKm > 100)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <TravelIcon color="action" />
                              </InputAdornment>
                            ),
                            inputProps: { 
                              ...numberInputProps,
                              min: 0,
                              max: 100
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth disabled={!isEditing}>
                          <InputLabel>Preferred Travel Method</InputLabel>
                          <Select
                            value={formData.preferredTravelMethod || ''}
                            label="Preferred Travel Method"
                            onChange={(e) => setFormData({ ...formData, preferredTravelMethod: e.target.value as any })}
                            startAdornment={
                              <InputAdornment position="start">
                                <TravelIcon color="action" />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="public_transit">Public Transit</MenuItem>
                            <MenuItem value="car">Car</MenuItem>
                            <MenuItem value="bicycle">Bicycle</MenuItem>
                            <MenuItem value="walking">Walking</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Maximum Travel Time (minutes)"
                          value={formData.maxTravelTimeMinutes || ''}
                          onChange={(e) => handleNumberInput(e.target.value, 'maxTravelTimeMinutes', 0, 180)}
                          disabled={!isEditing}
                          helperText="Maximum time you're willing to spend traveling"
                          error={formData.maxTravelTimeMinutes !== undefined && (formData.maxTravelTimeMinutes < 0 || formData.maxTravelTimeMinutes > 180)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ScheduleIcon color="action" />
                              </InputAdornment>
                            ),
                            inputProps: {
                              ...numberInputProps,
                              min: 0,
                              max: 180
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}; 